import { useI18N, useResponsive, useTheme } from "@sundae/react-hooks";
import { Balance, FadeInBasic, Text, toolkitCx } from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import {
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  ChartTypeRegistry,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
  registerables,
} from "chart.js";
import "chartjs-adapter-date-fns";
import cx from "classnames";
import transparentize from "polished/lib/color/transparentize";
import { FC, useMemo } from "react";
import { Chart } from "react-chartjs-2";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { getChartLegendFontConfig } from "../../../utils/charts.utils";
import { formatCurrencyToAda } from "../../../utils/number-format";
import { slotToUnix } from "../../../utils/slots.utils";
import TasteTestEmptyChartView from "../TasteTestEmptyChartView";

const {
  theme: { colors },
} = require("@sundae/tailwind-config");

const defaultPrimaryColor = colors.primary["DEFAULT"];
const defaultSecondaryColor = colors.secondary["DEFAULT"];

// Register the chart.js components required for a Line chart
ChartJS.register(
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  ...registerables
);

const TasteTestTVLChart: FC = () => {
  const { currentLocale, t } = useI18N("taste-test");
  const { theme } = useTheme();
  const responsive = useResponsive();
  const {
    state: { tasteTest, tasteTestPersonalization },
  } = useTasteTestContext();
  const tvl = useMemo(
    () =>
      new AssetAmount(
        tasteTest?.currentBalance?.tokenGathered.amount,
        tasteTest?.currentBalance?.tokenGathered.asset
      ),
    [tasteTest?.currentBalance?.tokenGathered.amount]
  );
  const chartColor = tasteTestPersonalization?.secondaryColor || defaultSecondaryColor;

  const isDark = theme === "dark";

  // Prepare the data for the chart
  const chartData = {
    labels: tasteTest?.historicalBalance?.times.map((time) =>
      new Date(slotToUnix(time) * 1000).getTime()
    ),
    datasets: [
      {
        label: "ADA Deposited",
        data: tasteTest?.historicalBalance?.tokenGathered.map((token) => Number(token)),
        fill: true,
        borderColor: chartColor,
        backgroundColor: transparentize(isDark ? 0.8 : 0.7, chartColor),
        pointBackgroundColor: chartColor,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ],
  };

  // Define chart options
  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    plugins: {
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (tooltipItem: TooltipItem<keyof ChartTypeRegistry>[]) => {
            return new Intl.DateTimeFormat(currentLocale, {
              dateStyle: "long",
              timeStyle: "short",
            }).format(new Date(tooltipItem[0].parsed.x));
          },
          label: (tooltipItem: TooltipItem<keyof ChartTypeRegistry>) => {
            return `${formatCurrencyToAda(Number(tooltipItem.parsed.y) / 1000000, currentLocale)}`;
          },
        },
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          color: "transparent",
        },
        min: chartData.labels?.[0],
        max: chartData.labels?.[chartData.labels?.length - 1],
        ticks: {
          ...getChartLegendFontConfig(isDark || false, responsive || {}),
          callback: (value) => {
            return new Intl.DateTimeFormat(currentLocale, {
              month: "short",
              day: "numeric",
            }).format(new Date(value));
          },
        },
        type: "time",
        time: {
          unit: "day",
        },
      },
      y: {
        grid: {
          color: "transparent",
        },
        ticks: {
          ...getChartLegendFontConfig(isDark || false, responsive || {}),
          callback: (value) => {
            return formatCurrencyToAda(Number(value) / 1000000, currentLocale, {
              notation: "compact",
            });
          },
        },
        beginAtZero: true,
      },
    },
  };

  /* -------------------------------------------------------------------------------------------------
   * Notice for no available TVL Information
   * -----------------------------------------------------------------------------------------------*/
  if (!tvl.amount) {
    return (
      <TasteTestEmptyChartView
        title={t("detail.priceCharts.tvl.noData", { context: "title" })}
        description={t("detail.priceCharts.tvl.noData")}
      />
    );
  }

  return (
    <FadeInBasic className="flex w-full flex-col gap-4">
      <div className="group relative mx-auto grid w-full max-w-3xl gap-4 lg:mx-0 lg:h-full">
        <div
          className={cx(
            "absolute left-16 top-0 rounded-lg bg-gray/90 group-hover:z-[-1] group-hover:opacity-40 dark:bg-gray-800/90 dark:group-hover:opacity-30",
            toolkitCx.layout.transition.opacity,
            toolkitCx.layout.spacing.both.md
          )}
        >
          <div
            className="flex flex-col"
            style={{
              color: tasteTestPersonalization?.primaryColor ?? defaultPrimaryColor,
            }}
          >
            <Balance abbreviate={false} size="lg" value={tvl} variant="current" />
            <Text tag="p" size="xs" variant="muted">
              {t("detail.priceCharts.tabs.tvl")}
            </Text>
          </div>
        </div>
        <Chart type="line" options={chartOptions} data={chartData} />
      </div>
    </FadeInBasic>
  );
};

export default TasteTestTVLChart;
